import { Injectable } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { YCanvas } from '../sessions/session/wb-canvas/fabric-utils';
import { ItemModel } from '../models/itemModel';
import { SessionSharedDataService } from './session-shared-data.service';
import { BreakoutRoomsManagementService } from './breakout-rooms/breakout-rooms-management.service';
import { DeviceAndBrowserDetectorService } from './device-and-browser-detector.service';
import { ActiveCanvasStateService, ActiveCanvasType } from './active-canvas-state.service';

/**
 * Service for checking if keyboard shortcuts should be skipped.
 * This service consolidates all keyboard shortcut checks to avoid
 * making MainToolbarService a central dependency for multiple services.
 */
@Injectable({
  providedIn: 'root',
})
export class KeyboardShortcutsCheckService {
  // Kept separately per canvas type (rather than one shared field) because the whiteboard
  // canvas and the gallery-view canvas (screen-share/full-screen-app annotations) are both
  // alive at the same time; whichever one last called setCanvasForType() must not clobber
  // the other's reference. The correct one is picked below based on ActiveCanvasStateService.
  private whiteboardCanvas?: YCanvas;
  private galleryCanvas?: YCanvas;

  constructor(
    private readonly sessionSharedDataService: SessionSharedDataService,
    private breakoutRoomsManagementService: BreakoutRoomsManagementService,
    private readonly overlayContainer: OverlayContainer,
    private readonly deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    private readonly activeCanvasStateService: ActiveCanvasStateService,
  ) {}

  /**
   * Set the fabric canvas reference for a given canvas type (whiteboard or gallery-view).
   */
  setCanvasForType(type: ActiveCanvasType, canvas: YCanvas | undefined): void {
    if (type === ActiveCanvasType.GALLERY_VIEW) {
      this.galleryCanvas = canvas;
    } else {
      this.whiteboardCanvas = canvas;
    }
  }

  /**
   * The canvas the user is currently interacting with, resolved from
   * ActiveCanvasStateService.activeCanvasType so shortcut checks always look at the *current*
   * canvas (whiteboard vs. the gallery-view canvas shared by screen-share/full-screen-app
   * annotations) instead of whichever canvas last happened to set itself.
   */
  private get fabricCanvas(): YCanvas | undefined {
    return this.activeCanvasStateService.activeCanvasType === ActiveCanvasType.GALLERY_VIEW
      ? this.galleryCanvas
      : this.whiteboardCanvas;
  }

  /**
   * Check if the element is an input area (textarea, input, or contenteditable)
   */
  private get isInputArea(): boolean {
    const activeElement = document.activeElement as HTMLElement;
    if (!activeElement) {
      return false;
    }

    const nodeName = activeElement.nodeName;

    // Check native form elements (TEXTAREA and INPUT don't have contenteditable attribute)
    if (nodeName === 'TEXTAREA' || nodeName === 'INPUT') {
      return true;
    }

    // Check if element is contenteditable (covers DIV, SPAN, and any other contenteditable elements)
    // This is more generic than checking specific node names
    if (activeElement.isContentEditable) {
      return true;
    }

    // Fallback for Quill editor (advanced-text-fragment) where contenteditable might be on a child element
    if (nodeName === 'DIV' && activeElement.parentElement?.id === 'advanced-text-fragment') {
      return true;
    }

    return false;
  }

  /**
   * Check if the event target is within the app root element.
   * Prevents shortcuts from firing when interacting with iframes or embedded content.
   */
  private isTargetAppRoot(target: HTMLElement): boolean {
    return !!target.closest('body.app-root-class');
  }

  /**
   * Checks if there's an active insertion point that should prevent keyboard shortcuts.
   * This includes textboxes, sticky notes in edit mode, etc.
   */
  private get hasActiveInsertionPoint(): boolean {
    // Check input areas (textarea, input, advanced text fragment)
    if (this.isInputArea) {
      return true;
    }

    // Check if any board item is in edit mode
    const activeObject = this.fabricCanvas?.getActiveObject();
    return (
      Boolean(activeObject && (activeObject as any).isEditing) ||
      // Making sure that when an embedded app is selected, even if not in edit mode, shortcuts are disabled.
      // This is to solve a usability issue since inside these embedded apps there could be an active text box (like a blinking cursor)
      // But still the user can't type in it until they click on the embedded app content to make it editable
      // So we prevent the shortcuts in this case so the user knows they need to click inside the embedded app to interact
      Boolean(
        activeObject &&
          [ItemModel.WebViewer, ItemModel.IFrame, ItemModel.Mario, ItemModel.Chat_GPT].includes(
            (activeObject as any)?.itemType,
          ),
      )
    );
  }

  /**
   * Check if a keyboard event has no modifier keys pressed.
   * This helper eliminates duplication of modifier key checks.
   *
   * @param event - Keyboard event
   * @returns true if no modifiers (shift, ctrl, meta, alt) are pressed
   */
  public hasNoModifiers(event: KeyboardEvent): boolean {
    return !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey;
  }

  /**
   * Single source of truth for checking if keyboard shortcuts should be skipped.
   * This method consolidates all the common checks that all shortcuts need:
   * - isDisableKeyEvent
   * - isInputArea
   * - is textbox/item in edit mode
   * - is target app root
   *
   * @param event - Optional keyboard event to check the target
   * @param target - Optional HTML element target (if not provided, will use event.target)
   * @param excludeEditMode - If true, only checks for input areas, not fabric object edit mode (useful for escape key handlers)
   * @returns true if shortcuts should be skipped
   */
  public shouldSkipKeyboardShortcuts(
    event?: KeyboardEvent,
    target?: HTMLElement,
    excludeEditMode = false,
  ): boolean {
    // Check if key events are disabled
    if (this.sessionSharedDataService.isDisableKeyEvents) {
      return true;
    }

    // Check if target is within app root (prevent shortcuts from iframes/embedded content)
    const eventTarget = target || (event?.target as HTMLElement);
    if (eventTarget && !this.isTargetAppRoot(eventTarget)) {
      return true;
    }

    if (this.breakoutRoomsManagementService.isBreakoutRoomsManagementScreenShown()) {
      return true;
    }

    // Only check for input areas, not fabric object edit mode (allows escape to exit edit mode)
    if (excludeEditMode) {
      return this.isInputArea;
    }

    // Check if there's an active insertion point (input area, textbox, or board item in edit mode)
    return this.hasActiveInsertionPoint;
  }

  public closeOpenMenus(): void {
    const container = this.overlayContainer.getContainerElement();
    const backdrops = container.querySelectorAll('.cdk-overlay-backdrop');
    backdrops.forEach((backdrop) => {
      backdrop.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
    });
  }

  /**
   * Check if keyboard shortcuts should be skipped for current session view-specific operations.
   * This includes all common checks plus a session view check.
   *
   * @param isSessionViewAllowedForShortcuts - If true, the session view is allowed for shortcuts
   * @param event - Optional keyboard event to check the target
   * @param target - Optional HTML element target (if not provided, will use event.target)
   * @param excludeEditMode - If true, only checks for input areas, not fabric object edit mode (useful for escape key handlers)
   * @returns true if shortcuts should be skipped
   */
  public shouldSkipKeyboardShortcutsForSessionView(
    isSessionViewAllowedForShortcuts: boolean,
    event?: KeyboardEvent,
    target?: HTMLElement,
    excludeEditMode = false,
  ): boolean {
    // Use unified shortcut skip check (includes all common checks)
    if (this.shouldSkipKeyboardShortcuts(event, target, excludeEditMode)) {
      return true;
    }

    // Check if the session view is allowed for shortcuts
    if (!isSessionViewAllowedForShortcuts) {
      return true;
    }
    return false;
  }

  /**
   * Get the Alt key sign based on the operating system.
   * Returns '⌥' for macOS and 'Alt' for other operating systems.
   *
   * @returns The Alt key sign string
   */
  public getAltKeySign(): string {
    return this.deviceAndBrowserDetectorService.isMacOS() ? '⌥' : 'Alt';
  }
}
